import React, { useEffect, useMemo } from "react";
import { useLocalContext } from "../../context/LocalContext";

const TAGS = ["Digital-first", "Fair review", "Clear timelines"];

const Refund = () => {
  const { webinfo = {} } = useLocalContext();
  const brand = webinfo?.name || "Our Store";

  useEffect(() => {
    if (brand) {
      document.title = `Refund & Return Policy | ${brand}`;
    }
  }, [brand]);

  const lastUpdated = useMemo(() => {
    const d = new Date();
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  }, []);

  const sections = [
    { id: "digital", title: "Digital Products (License Keys & Downloads)" },
    { id: "physical", title: "Physical Products (If Applicable)" },
    { id: "damaged", title: "Damaged, Incomplete or Incorrect Orders" },
    { id: "cancellations", title: "Order Cancellations" },
    { id: "timeline", title: "Refund Method & Timeline" },
    { id: "nonrefundable", title: "Non-Refundable Items" },
    { id: "returnshipping", title: "Return Shipping (Physical Items Only)" },
    { id: "support", title: "Technical & Activation Support" },
    { id: "fraud", title: "Fraud, Chargebacks & Abuse" },
    { id: "howto", title: "How to Request a Refund" },
    { id: "updates", title: "Policy Updates" },
    { id: "contact", title: "Contact" },
  ];

  // Best-effort website URL
  let siteUrl = "";
  if (typeof window !== "undefined") {
    siteUrl = window.location.origin;
  }

  return (
    <div className="t-page">
      {/* Hero */}
      <header className="t-hero">
        <div className="t-hero__eyebrow">LEGAL</div>
        <h1 className="t-hero__title">Refund &amp; Return Policy</h1>
        <p className="t-hero__subtitle">
          This page explains when refunds, replacements, or credits may be
          available, how we handle digital license issues, and what to expect if
          something goes wrong with your order.
        </p>
        <div className="t-hero__meta">Last updated: {lastUpdated}</div>
        <div className="t-hero__tags">
          {TAGS.map((t) => (
            <span key={t} className="t-pill">
              {t}
            </span>
          ))}
        </div>
      </header>

      {/* Grid */}
      <section className="t-grid">
        {/* Sidebar TOC */}
        <aside className="t-toc" aria-label="On this page">
          <div className="t-card t-toc__card">
            <div className="t-toc__title">ON THIS PAGE</div>
            <nav>
              <ol className="t-toc__list">
                {sections.map((s, i) => (
                  <li key={s.id}>
                    <a href={`#${s.id}`}>
                      <span className="t-toc__num">{i + 1})</span> {s.title}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>
          </div>
        </aside>

        {/* Content */}
        <article className="t-card t-content">
          <p>
            <strong>{brand}</strong> primarily offers{" "}
            <strong>digital software products and license keys</strong>. Because
            these products are sensitive and can’t be “returned” once revealed,
            some purchases are not refundable after delivery. This policy
            explains our approach so you know what is and isn’t possible before
            you buy.
          </p>

          {/* 1 – Digital */}
          <section id="digital" className="t-sec">
            <h2 className="t-h2">
              Digital Products (License Keys &amp; Downloads)
            </h2>
            <p>
              In most situations,{" "}
              <strong>
                digital sales are final once the license key or digital content
                has been issued, shown, or activated
              </strong>
              . Digital keys are unique and generally cannot be re-sold or
              safely assigned to someone else after they are revealed.
            </p>
            <p>
              If you believe there is a{" "}
              <strong>problem with your key or delivery</strong> (for example:
              “invalid key”, “already used”, or you never received the license
              email), please contact us within <strong>5 days</strong> of
              delivery at{" "}
              <a href={`mailto:${webinfo.email || ""}`}>
                {webinfo.email || "support@example.com"}
              </a>
              .
            </p>
            <p>To investigate, we may ask you to provide:</p>
            <ul className="t-list">
              <li>Clear screenshots of the error message or activation screen.</li>
              <li>The email address and order number used during purchase.</li>
              <li>
                Basic system details (operating system, region, and any
                existing security/antivirus tools).
              </li>
            </ul>
            <p>
              If our checks (and, where needed, the vendor’s verification)
              confirm that:
            </p>
            <ul className="t-list">
              <li>the key was <strong>defective</strong>, or</li>
              <li>
                there was a <strong>fulfilment error on our side</strong>,
              </li>
            </ul>
            <p>we will offer one of the following:</p>
            <ul className="t-list">
              <li>
                A <strong>replacement key</strong> of the same or equivalent
                product (where reasonably available), or
              </li>
              <li>
                If a replacement is not possible, a{" "}
                <strong>refund or store credit</strong>, aligned with this
                policy and any applicable legal requirements.
              </li>
            </ul>
            <p>
              General “change of mind” scenarios — such as choosing the wrong
              product, non-compatible device/OS, unused time on a subscription
              you no longer want, or region restrictions clearly stated on the
              product page — are typically{" "}
              <strong>not eligible for a refund</strong>, unless required by
              applicable law.
            </p>
          </section>

          {/* 2 – Physical */}
          <section id="physical" className="t-sec">
            <h2 className="t-h2">Physical Products (If Applicable)</h2>
            <p>
              Our business is focused on digital products. If we ever offer{" "}
              <strong>physical goods</strong> (for example, accessories or
              printed materials), those items will be clearly marked on the
              product page and this section will apply to them.
            </p>
            <p>
              Unless otherwise stated, physical items may be eligible for return
              within <strong>7 days</strong> of delivery if they are:
            </p>
            <ul className="t-list">
              <li>Unused and in a resellable, original condition.</li>
              <li>
                Returned with the original packaging, accessories, and
                documentation.
              </li>
              <li>Accompanied by valid proof of purchase.</li>
            </ul>
            <p>
              Please contact us before sending anything back so we can provide
              return instructions and, where needed, a return authorization.
            </p>
          </section>

          {/* 3 – Damaged / incorrect */}
          <section id="damaged" className="t-sec">
            <h2 className="t-h2">Damaged, Incomplete or Incorrect Orders</h2>
            <p>
              If your order appears to be{" "}
              <strong>incorrect, incomplete, or damaged</strong>, please notify
              us within <strong>5 days</strong> of delivery at{" "}
              <a href={`mailto:${webinfo.email || ""}`}>
                {webinfo.email || "support@example.com"}
              </a>
              .
            </p>
            <p>We may request:</p>
            <ul className="t-list">
              <li>
                Screenshots or a short description of the issue for digital
                products.
              </li>
              <li>
                Photos of the packaging and item for physical goods, in case a
                courier or logistics claim is necessary.
              </li>
            </ul>
            <p>
              Once we confirm the issue, we will offer{" "}
              <strong>replacement items, a partial refund, a full refund, or
              store credit</strong>, depending on the case and product type.
            </p>
          </section>

          {/* 4 – Cancellations */}
          <section id="cancellations" className="t-sec">
            <h2 className="t-h2">Order Cancellations</h2>
            <ul className="t-list">
              <li>
                <strong>Before Fulfilment:</strong> You may request a
                cancellation if your digital key has not yet been generated or
                delivered, or if a physical parcel has not yet been handed to
                the carrier. We will confirm whether cancellation is possible at
                that stage.
              </li>
              <li>
                <strong>After Fulfilment:</strong> Once a license key is sent or
                a physical parcel is dispatched, the order is generally{" "}
                <strong>no longer cancellable</strong>.
              </li>
              <li>
                <strong>Duplicate Payments/Orders:</strong> If you accidentally
                placed the same order more than once, contact us quickly. If
                extra keys have not been used and no additional parcel has been
                shipped, we will review options for cancellation and refund.
              </li>
            </ul>
          </section>

          {/* 5 – Timeline */}
          <section id="timeline" className="t-sec">
            <h2 className="t-h2">Refund Method &amp; Timeline</h2>
            <p>
              When a refund is approved, it will normally be issued back to the{" "}
              <strong>original payment method</strong>. In some cases, and with
              your agreement, we may offer{" "}
              <strong>store credit or an alternative method</strong> instead
              (for example, if the original method cannot accept refunds).
            </p>
            <p>
              Once we process the refund, banks and payment providers typically
              take around <strong>5–10 business days</strong> to display the
              amount on your statement. This timeframe is controlled by your
              provider, not by <strong>{brand}</strong>.
            </p>
          </section>

          {/* 6 – Non-refundable */}
          <section id="nonrefundable" className="t-sec">
            <h2 className="t-h2">Non-Refundable Items</h2>
            <p>
              Unless explicitly stated otherwise or required by applicable law,
              the following are generally{" "}
              <strong>not eligible for a refund</strong>:
            </p>
            <ul className="t-list">
              <li>
                Digital license keys that have been issued, revealed, or
                activated.
              </li>
              <li>Delivered downloads or digital content.</li>
              <li>
                Requests raised after the stated claim window (for example, more
                than 5 days after key delivery for activation issues).
              </li>
              <li>Gift cards, vouchers, or store credit balances.</li>
              <li>
                Subscription renewals where the renewal period has already
                started and access has continued.
              </li>
              <li>Items clearly marked as “final sale” or non-refundable.</li>
            </ul>
          </section>

          {/* 7 – Return shipping */}
          <section id="returnshipping" className="t-sec">
            <h2 className="t-h2">Return Shipping (Physical Items Only)</h2>
            <p>
              For physical products, if a return is approved and the reason is{" "}
              <strong>not</strong> due to our error (for example, change of
              mind), you are typically responsible for:
            </p>
            <ul className="t-list">
              <li>Return shipping costs and any associated fees.</li>
              <li>
                Using a trackable shipping service and keeping proof of postage.
              </li>
            </ul>
            <p>
              We cannot process refunds for returned items that are lost in
              transit without delivery confirmation. Unauthorized or unsolicited
              returns sent without contacting us first may be refused.
            </p>
          </section>

          {/* 8 – Support */}
          <section id="support" className="t-sec">
            <h2 className="t-h2">Technical &amp; Activation Support</h2>
            <p>
              Many issues can be solved without a refund. If you’re unsure
              whether a problem relates to the license, your device, or
              installation steps, please reach out to us first at{" "}
              <a href={`mailto:${webinfo.email || ""}`}>
                {webinfo.email || "support@example.com"}
              </a>
              .
            </p>
            <p>
              We can help verify your license, check basic system compatibility,
              and share relevant guidance from the software vendor where
              needed—often resolving the problem faster than a refund request.
            </p>
          </section>

          {/* 9 – Fraud */}
          <section id="fraud" className="t-sec">
            <h2 className="t-h2">Fraud, Chargebacks &amp; Abuse</h2>
            <p>
              To protect all customers and our partners,{" "}
              <strong>{brand}</strong> may decline or revoke refunds in cases of
              suspected fraud or abuse, including:
            </p>
            <ul className="t-list">
              <li>Unusual or repeated patterns of refund or key claims.</li>
              <li>Use of stolen, disputed, or unauthorized payment methods.</li>
              <li>
                Repeated chargebacks without prior attempts to contact us or
                resolve the issue directly.
              </li>
            </ul>
            <p>
              In serious cases, we may disable licenses, restrict account
              access, and, where appropriate, work with payment processors or
              relevant authorities. Whenever possible, we strongly encourage you
              to <strong>contact us first</strong> so we can investigate and
              help before you file a chargeback.
            </p>
          </section>

          {/* 10 – How to request */}
          <section id="howto" className="t-sec">
            <h2 className="t-h2">How to Request a Refund</h2>
            <p>
              To request a refund, replacement, or review of your order, please
              email us at{" "}
              <a href={`mailto:${webinfo.email || ""}`}>
                {webinfo.email || "support@example.com"}
              </a>{" "}
              and include:
            </p>
            <ul className="t-list">
              <li>Your full name and the email used at checkout.</li>
              <li>Your order number and purchase date.</li>
              <li>
                The product name(s) and a clear description of the problem or
                reason for your request.
              </li>
              <li>
                For digital/activation issues: screenshots, error messages, and
                any steps you’ve already tried.
              </li>
              <li>
                For physical items (if applicable): confirmation of the item’s
                condition and photos if there is damage.
              </li>
            </ul>
            <p>
              We aim to acknowledge refund or review requests within{" "}
              <strong>2 business days</strong> and will let you know the next
              steps or a decision as soon as reasonably possible.
            </p>
          </section>

          {/* 11 – Updates */}
          <section id="updates" className="t-sec">
            <h2 className="t-h2">Policy Updates</h2>
            <p>
              We may revise this Refund &amp; Return Policy from time to time
              (for example, if our product range changes or if the law requires
              an update). The “Last updated” date at the top of this page shows
              the latest version. Continuing to use our Services after changes
              take effect means you accept the updated policy.
            </p>
          </section>

          {/* 12 – Contact */}
          <section id="contact" className="t-sec">
            <h2 className="t-h2">Contact</h2>
            <p>
              If you’re unsure whether your order is eligible for a refund or
              replacement, or you simply want clarification before buying,
              you’re always welcome to reach out.
            </p>
            <address className="t-address">
              <strong>{brand}</strong>
              <br />
              {webinfo.address && (
                <>
                  {webinfo.address}
                  <br />
                </>
              )}
              Email:{" "}
              <a href={`mailto:${webinfo.email || ""}`}>
                {webinfo.email || "support@example.com"}
              </a>
              <br />
              {webinfo.phone && (
                <>
                  Phone: <a href={`tel:${webinfo.phone}`}>{webinfo.phone}</a>
                  <br />
                </>
              )}
              {siteUrl && (
                <>
                  Website:{" "}
                  <a href={siteUrl} target="_blank" rel="noopener noreferrer">
                    {siteUrl}
                  </a>
                </>
              )}
            </address>
          </section>
        </article>
      </section>
    </div>
  );
};

export default Refund;
